import { motion } from "framer-motion";
import { Activity, Footprints, RotateCw, ShieldCheck } from "lucide-react";

const IMG_KAT = "/photos/katvr-room.jpg";
const IMG_CHAIR = "/photos/chaise-360.jpg";

const STATS = [
  { value: "5", label: "TAPIS KAT VR" },
  { value: "360°", label: "ROTATION CHAISE" },
  { value: "6", label: "JOUEURS SIMULTANÉS" },
  { value: "12+", label: "EXPÉRIENCES" },
];

const KAT_SPECS = [
  "Déplacement omnidirectionnel naturel",
  "Harnais de maintien ajustable",
  "Chaussures & patins dédiés",
  "Casque haute résolution inclus",
];

const CHAIR_SPECS = [
  "Rotation gyroscopique complète 360°",
  "Vibrations haptiques synchronisées",
  "Simulations spatiales & extrêmes",
  "Sécurité renforcée, dès 12 ans",
];

export const EquipmentShowcase = () => (
  <section id="equipements" data-testid="equipment-section" className="relative bg-surface py-24 sm:py-28">
    <div className="mx-auto max-w-7xl px-5">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
      >
        <p className="font-hud text-xs uppercase tracking-[0.35em] text-cyan-400">
          {"// 02 — Équipements"}
        </p>
        <h2 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
          Du matériel <span className="text-glow-magenta text-fuchsia-500">d'exception</span>
        </h2>
      </motion.div>

      <div className="mt-14 grid gap-8 lg:grid-cols-2">
        <motion.article
          data-testid="equipment-katvr-card"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="pointer-events-none absolute -inset-8 bg-[radial-gradient(circle_at_50%_0%,rgba(0,240,255,0.12),transparent_65%)]" />
          <div className="clip-frame relative border border-cyan-500/25 bg-void">
            <div className="relative h-64 overflow-hidden sm:h-72">
              <img src={IMG_KAT} alt="Les 5 tapis KAT VR de la salle Immersion Plus" loading="lazy" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-void to-transparent" />
              <span className="absolute right-4 top-4 font-orbitron text-5xl font-black text-cyan-400 text-glow-cyan">
                ×5
              </span>
            </div>
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-3">
                <Footprints className="h-5 w-5 text-cyan-400" />
                <h3 className="font-display text-xl font-bold uppercase tracking-wide text-slate-100">
                  KAT VR Walk Mini
                </h3>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Le tapis omnidirectionnel de référence : marchez, courez et
                accroupissez-vous dans le jeu pendant que le harnais vous
                maintient en sécurité. 5 postes synchronisés pour le multijoueur.
              </p>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {KAT_SPECS.map((s) => (
                  <li key={s} className="flex items-start gap-2.5 font-hud text-[11px] uppercase tracking-wider text-slate-300">
                    <Activity className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-400" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.article>

        <motion.article
          data-testid="equipment-chair360-card"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="relative"
        >
          <div className="pointer-events-none absolute -inset-8 bg-[radial-gradient(circle_at_50%_0%,rgba(255,0,122,0.12),transparent_65%)]" />
          <div className="clip-frame relative border border-fuchsia-500/25 bg-void">
            <div className="relative h-64 overflow-hidden sm:h-72">
              <img src={IMG_CHAIR} alt="La chaise VR 360° Immersion Plus" loading="lazy" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-void to-transparent" />
              <span className="absolute right-4 top-4 font-orbitron text-5xl font-black text-fuchsia-500 text-glow-magenta">
                360°
              </span>
            </div>
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-3">
                <RotateCw className="h-5 w-5 text-fuchsia-500" />
                <h3 className="font-display text-xl font-bold uppercase tracking-wide text-slate-100">
                  Chaise VR 360°
                </h3>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                La chaise gyroscopique qui tourne sur tous les axes : loopings,
                tonneaux et chutes libres reproduits à l'identique. Sensations
                garanties pour les amateurs d'adrénaline.
              </p>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {CHAIR_SPECS.map((s) => (
                  <li key={s} className="flex items-start gap-2.5 font-hud text-[11px] uppercase tracking-wider text-slate-300">
                    <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fuchsia-500" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.article>
      </div>

      <motion.div
        data-testid="equipment-stats"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="mt-16 grid grid-cols-2 gap-px border border-line bg-line lg:grid-cols-4"
      >
        {STATS.map((s) => (
          <div key={s.label} className="bg-void px-6 py-8 text-center">
            <div className="font-orbitron text-3xl font-black text-cyan-400 sm:text-4xl">
              {s.value}
            </div>
            <div className="mt-2 font-hud text-[10px] uppercase tracking-[0.25em] text-slate-500">
              {s.label}
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);
